import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { ControlButton } from '@/pages/CADPage/components/ControlPanel/ControlButton'
import { FullModelToggledOffIcon } from '@/components/icons/FullModelToggledOffIcon'
import { FullModelToggledOnIcon } from '@/components/icons/FullModelToggledOnIcon'
import { useCADPageStore } from '@/state'
import { useRefreshCADVisibility } from '@/pages/CADPage/components/ControlPanel/hooks/useRefreshCADVisibility'

/**
 * Controls whether users see the complete model assembly or
 * only the parts relevant to the current operation (isolated view).
 */

interface ToggleModelButtonProps {
  disabled?: boolean
}

export const ToggleModelButton = ({ disabled }: ToggleModelButtonProps) => {
  const { refreshCADVisibility, isLoading } = useRefreshCADVisibility()

  const setCADPageState = useCADPageStore(useShallow((state) => state.setState))

  const isToggledOn = useCADPageStore((state) => state.showFullModel)

  const Icon = useMemo(() => {
    return isToggledOn ? FullModelToggledOnIcon : FullModelToggledOffIcon
  }, [isToggledOn])

  const title = useMemo(() => {
    return isToggledOn ? 'Model off' : 'Model on'
  }, [isToggledOn])

  const onToggle = useCallback(() => {
    setCADPageState({
      showFullModel: !isToggledOn,
    })

    refreshCADVisibility({
      showFullModel: !isToggledOn,
    })
  }, [isToggledOn, refreshCADVisibility, setCADPageState])

  return (
    <ControlButton
      title={title}
      icon={
        <Icon className="w-5 h-5" color={isToggledOn ? '#3054EF' : undefined} />
      }
      onClick={onToggle}
      disabled={disabled || isLoading}
    />
  )
}
